import React from 'react'
import { defineMessage, FormattedMessage, MessageDescriptor } from 'react-intl'

import {
  ELIGIBILITY_TYPES,
  EligibilityType,
  excludedServices,
  RESOURCE_COORDINATOR_SCHEDULE_CALL_LINK,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  consentToSMSMessage,
  invalidEligibilityDobMessage,
  invalidEligibilityFirstNameMessage,
  invalidEligibilityLastNameMessage,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import { WORKLIFE_SERVICE_IDS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/workLifeConstants'
import { getHelpEmailAddress } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/commonUtils'
import { generateServicesNotCovered } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/customerUtils'
import { getCarelonUrl } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { eligibilityDelayNotice } from '@lyrahealth-inc/shared-app-logic/src/features/eligibility/messages'
import { EligibilityField } from '@lyrahealth-inc/shared-app-logic/src/features/eligibility/types'
import { GetCustomerCopyFunc } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import { LocationMatcherDisplayFormats } from '@lyrahealth-inc/shared-app-logic/src/models/searchForCare/SearchForCare'

import { getDefaultCostMessagingModalText } from './util/DefaultCoverageTexts'
import { BodyText } from '../atoms/bodyText/BodyText'
import { CoverageNotice } from '../atoms/coverageNotice/CoverageNotice'
import {
  WorkLifeIconDependentCare,
  WorkLifeIconFinancial,
  WorkLifeIconIdentity,
  WorkLifeIconLaw,
} from '../atoms/icons/WorkLifeIcons'
import { Link } from '../atoms/link/Link'
import { Subhead } from '../atoms/subhead/Subhead'
import { BasicInfoField } from '../organisms/setUpAccountForm/basicInfoMetadata'
import { SubheadSize } from '../styles/typeStyles'
import { mailTo } from '../utils/utils'

export const servicesNotCovered = generateServicesNotCovered({
  excludedServices,
})

export const servicesCovered =
  'Lyra can help with stress, anxiety, depression, relationship issues, sleep disorders, and other common behavioral issues that can be treated through short-term outpatient behavioral health services.'

export const onsiteDescription = (
  <FormattedMessage
    defaultMessage='For your convenience, you may have the option to meet with a provider at your office. To schedule an onsite appointment, select a location below. Be sure to select a location where you work or you can easily access.'
    description='Body text that explains that you can select a location below this text to meet with a provider onsite if it is available. The text also emphasizes that the user should choose a location where they work or can easily get to.'
  />
)

export const onsiteAlternativeDescription = (
  <FormattedMessage
    defaultMessage='If you don’t see providers in your area, you may not have an onsite provider at your office or the onsite provider may not have availability over the next two weeks. We suggest trying a different care option above.'
    description="Body text that explains if there is no offsite location below the text that is in the user's area, then they should try using different care option above."
  />
)

export const DefaultCustomerRedirectionHeaderText: React.FC<{ customerName: string; boldCustomerName?: boolean }> = ({
  customerName,
  boldCustomerName = false,
}) => (
  // customerName should not be translated
  <FormattedMessage
    defaultMessage='Your access through {customerName}'
    description='Header in a dialogue box that asks user select which employee subpopulation or specific health plan applies to them'
    values={{
      customerName: boldCustomerName ? (
        <Subhead bold={true} text={customerName} size={SubheadSize.LARGE} />
      ) : (
        customerName
      ),
    }}
  />
)

export const getCarelonDefaultWorkLifeServices = (customerName: string) => {
  const carelonUrl = getCarelonUrl(customerName)
  return {
    LEGAL_SERVICES: {
      identifiers: WORKLIFE_SERVICE_IDS.LEGAL_SERVICES,
      title: 'Legal resources',
      iconSvg: <WorkLifeIconLaw size={48} />,
      paragraph: 'Comprehensive legal support:',
      bullets: [
        'Family law and other legal consultations',
        'Discounted attorney fees',
        'On-demand legal resources (forms, templates, and more)',
      ],
      CTA: 'Access legal resources',
      PATH: carelonUrl,
    },
    FINANCIAL_SERVICES: {
      identifiers: WORKLIFE_SERVICE_IDS.FINANCIAL_SERVICES,
      title: 'Financial wellbeing',
      iconSvg: <WorkLifeIconFinancial size={48} />,
      paragraph: 'Your partner in financial success:',
      bullets: [
        'Financial planning for retirement, education, and goals',
        'Get help managing loans, bankruptcy, and estate planning',
        'Personal and family finances, such as child support and tax preparation.',
      ],
      CTA: 'Plan your finances',
      PATH: carelonUrl,
    },
    IDENTITY_THEFT: {
      identifiers: WORKLIFE_SERVICE_IDS.IDENTITY_THEFT,
      title: 'Identity theft support',
      iconSvg: <WorkLifeIconIdentity size={48} />,
      paragraph: 'Protect yourself and your family with:',
      bullets: ['Comprehensive resources and support', 'Expert guidance', 'Support to navigate your options'],
      CTA: 'Access legal resources',
      PATH: carelonUrl,
    },
    DEPENDENT_CARE: {
      identifiers: WORKLIFE_SERVICE_IDS.DEPENDENT_CARE,
      title: 'Work-life benefits',
      iconSvg: <WorkLifeIconDependentCare size={48} />,
      paragraph: 'Comprehensive support for your caregiving journey:',
      bullets: [
        'Child, elder, and adult caregiver resources',
        'Support for adoptions, parenting, pet care, and more family needs (adoptions, parenting, pet care)',
        'Educational resources and personal development',
      ],
      CTA: ' Access work-life benefits',
      PATH: carelonUrl,
    },
  }
}

export const defaultCustomer: GetCustomerCopyFunc = ({
  numberOfSessions,
  numberOfSessionsByProgram,
  displayVisitsPerIssuePerYearByProgram,
  considerMeds,
  customerName,
  customerDisplayName,
  isCustomerDynamicSessionLimitEnabled = false,
  isLyraForLyrians2026Enabled = false,
}) => {
  const careTeamHelpEmail = getHelpEmailAddress({
    customerName: customerName ?? '',
    // Currently the care team email link is only used for blocking eligibility checks, which does not apply to international users
    isInternationalUser: false,
    isLyraForLyrians2026Enabled,
  })
  const onCareLinkPress = () => mailTo(careTeamHelpEmail)

  const emailLink = <Link text={careTeamHelpEmail} onPress={onCareLinkPress} underline={true} />
  const CARELON_WORKLIFE_SERVICES = getCarelonDefaultWorkLifeServices(customerName || '')
  return {
    displaySessionLimit: true,
    // TODO - Add faqQuestions: [],
    selfCareAppButtons: [],
    eligibilityQuestionSubtitle: eligibilityDelayNotice,
    generateEligibilityQuestionTitle: () =>
      defineMessage({
        defaultMessage:
          'Are you {employeeType, select, associate {an associate} member {a member} partner {a partner} teammate {a teammate} teammember {a team member} employee {an employee} other {an employee}}{hasCustomerName, select, yes { of {customerName}} other {}}?',
        description: 'Asking the user if they are the employee of a company or organization or member of a plan',
      }),
    generateEligibilityDependentTitle: () =>
      defineMessage({
        defaultMessage: `Provide the {hasCustomerName, select, yes {{customerName} {employeeType, select, associate {associate's} member {member's} partner {partner's} teammate {teammate's} teammember {team member's} employee {employee's} other {employee's}}} other {{employeeType, select, associate {associate's} member {member's} teammate {teammate's} teammember {team member's} employee {employee's} other {employee's}}}} information`,
        description: 'Question for whom the benefit provider is',
      }),
    eligibilityCheckSubmissionErrors: {
      [EligibilityField.EMPLOYEE_FIRST_NAME]: invalidEligibilityFirstNameMessage,
      [EligibilityField.EMPLOYEE_LAST_NAME]: invalidEligibilityLastNameMessage,
      [EligibilityField.EMPLOYEE_DATE_OF_BIRTH]: invalidEligibilityDobMessage,
    },
    generateEligibilityCheckSubmissionErrors: (
      eligibilityType?: EligibilityType,
      checkAdultDependentEligibilityUsingDependentInfo?: boolean,
    ): {
      [fieldName: string]: MessageDescriptor
    } => {
      const isDependent = eligibilityType === ELIGIBILITY_TYPES.DEPENDENT
      if (isDependent && !checkAdultDependentEligibilityUsingDependentInfo) {
        return {
          [EligibilityField.EMPLOYEE_FIRST_NAME]: invalidEligibilityFirstNameMessage,
          [EligibilityField.EMPLOYEE_LAST_NAME]: invalidEligibilityLastNameMessage,
          [EligibilityField.EMPLOYEE_DATE_OF_BIRTH]: invalidEligibilityDobMessage,
        }
      } else {
        return {
          [BasicInfoField.USER_FIRST_NAME]: invalidEligibilityFirstNameMessage,
          [BasicInfoField.USER_LAST_NAME]: invalidEligibilityLastNameMessage,
          [BasicInfoField.USER_DATE_OF_BIRTH]: invalidEligibilityDobMessage,
        }
      }
    },
    generateConfirmEligibilityFormDefaultError: (isHealthPlanDirect: boolean, hasBlockingEligibilityCheck: boolean) =>
      isHealthPlanDirect ? (
        <FormattedMessage
          defaultMessage='Your name and date of birth must exactly match the information on file with your sponsor. If you’re unsure, please contact a Lyra Care Navigator and they can help determine the problem.'
          description='Tips for confirming eligibility with isHealthPlanDirect set to true'
        />
      ) : hasBlockingEligibilityCheck ? (
        <FormattedMessage
          defaultMessage='Your name and date of birth must exactly match the information on file with your employer. If you’re not sure what’s wrong, please reach out to {emailLink} for support.'
          description='Tips for confirming eligibility with hasBlockingEligibilityCheck set to true'
          values={{ emailLink }}
        />
      ) : (
        <FormattedMessage
          defaultMessage='Your name and date of birth must exactly match the information on file with your employer. If you’re not sure what’s wrong, you can continue and confirm eligibility later.'
          description='Tips for confirming eligibility'
        />
      ),
    accountLockedText: defineMessage({
      defaultMessage: 'This account may be locked, or may not exist. Please try to reset your password.',
      description: 'Text displayed when user account is locked after failed attempts',
    }),
    dependentButtonText: defineMessage({
      defaultMessage: 'No, I’m a dependent',
      description: 'Declaring the current user is a dependent of an eligible user',
    }),
    shouldDisplayBCTProviderBenefits: true,
    legalNotice: <CoverageNotice considerMeds={considerMeds} sessionLimit={numberOfSessions} />,
    modalityQuestionnaireText: {
      modalityLabelAddendums: {
        video: defineMessage({
          defaultMessage:
            'Due to concerns about COVID-19 and increased interest in meeting by video, we’ve expanded the availability of our video providers.',
          description:
            'Shown when client indicates their preference to meet by video or in person, when searching for a provider',
        }),
      },
    },
    passwordResetFailedText: defineMessage({
      defaultMessage:
        "Please try again. Your new password cannot be one you've previously used and cannot contain parts of your email.",
      description:
        'Banner text informing the user their attempt to reset their password has failed or did not meet the requirements',
    }),
    servicesNotCovered,
    servicesCovered:
      'Lyra can help with stress, anxiety, depression, relationship issues, sleep disorders, and other common behavioral issues that can be treated through short-term outpatient behavioral health services.',
    shouldDisplayCareTeamContactInfo: true,
    smsConsent: {
      primaryConsentText: <FormattedMessage {...consentToSMSMessage} />,
    },
    locationFinder: {
      onsiteFormat: LocationMatcherDisplayFormats.RADIO_BUTTONS_ONLY,
      onsiteDescription: (
        <>
          <BodyText>{onsiteDescription}</BodyText>
          <BodyText>{onsiteAlternativeDescription}</BodyText>
        </>
      ),
    },
    scheduleResourceCoordinatorCallLink: RESOURCE_COORDINATOR_SCHEDULE_CALL_LINK,

    /* Worklife services that a customer can access, assuming full eligibility.
     * Not all customers have access to all services, but this is determined on component render, so we provide all
     * possible service copies to the customer without taking actual offered services into account.
     *
     * Customers can customize their service copy by passing in an array of different services (which will be displayed
     * in the order of items in the array). They can also generate entirely new copy for a given service so long as the
     * ID of the service object maps to a valid worklife service identifier used in the worklife customer property.
     *
     * e.g. This would constitute a valid custom service:
     * { identifiers: WORKLIFE_SERVICE_IDS.IDENTITY_THEFT, title: 'Legal Services (for U.S. employees only)', ... }
     * This would not constitute a valid custom service because 'legalServices' is not a valid worklife service ID:
     * { identifiers: { id: 'legalServices' }, title: 'Legal Services (for U.S. employees only)', ... }
     */
    workLifeServices: [
      CARELON_WORKLIFE_SERVICES.LEGAL_SERVICES,
      CARELON_WORKLIFE_SERVICES.FINANCIAL_SERVICES,
      CARELON_WORKLIFE_SERVICES.IDENTITY_THEFT,
      CARELON_WORKLIFE_SERVICES.DEPENDENT_CARE,
    ],
    workLifeServicesCustomDescription: undefined,
    costMessagingModalText: getDefaultCostMessagingModalText(
      numberOfSessions ?? '',
      customerDisplayName ? customerDisplayName : customerName ?? '',
      numberOfSessionsByProgram,
      displayVisitsPerIssuePerYearByProgram,
      isCustomerDynamicSessionLimitEnabled,
      customerName,
    ),
  }
}
